import { useState, useMemo } from "react";
import type { QuoteRequestListItemDto } from "@semo/api-contracts";
import { isQuoteRequestClosed } from "../../domain/quoteRequestClosed";

export type RespondedQuoteFilter = "all" | "IN_PROGRESS" | "CLOSED";

/**
 * 참여 프로젝트 탭 거름·숫자.
 *
 * 공급사 API 는 마감을 `isClosed` 로 따로 주고 `status` 는 IN_PROGRESS / CONTRACT_IN_PROGRESS 만
 * 준다. 예전엔 `status` 로만 갈라서 마감된 공고가 «진행중» 에 들어가고, 계약 진행 중인 공고는
 * 어느 탭에도 안 들어갔다(A3-09). 이제 마감이면 «마감», 아니면 «진행중» — 모든 공고가 한 탭에
 * 들어가고, 카드 뱃지도 같은 규칙(마감이면 CLOSED)을 쓴다.
 */
export function useRespondedQuoteFilters(requests: QuoteRequestListItemDto[]) {
  const [activeFilter, setActiveFilter] = useState<RespondedQuoteFilter>("all");

  const classified = useMemo(
    () =>
      requests.map((request) => {
        const closed = isQuoteRequestClosed(request);
        return {
          bucket: closed ? ("CLOSED" as const) : ("IN_PROGRESS" as const),
          request: closed ? { ...request, status: "CLOSED" as const } : request,
        };
      }),
    [requests],
  );

  const counts = useMemo(() => {
    const closed = classified.filter((c) => c.bucket === "CLOSED").length;
    return {
      all: classified.length,
      IN_PROGRESS: classified.length - closed,
      CLOSED: closed,
    };
  }, [classified]);

  const filteredRequests = useMemo(() => {
    const picked =
      activeFilter === "all"
        ? classified
        : classified.filter((c) => c.bucket === activeFilter);
    return picked.map((c) => c.request);
  }, [classified, activeFilter]);

  return {
    activeFilter,
    setActiveFilter,
    filteredRequests,
    counts,
  };
}
